import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useDialogFocus } from "../lib/dialogFocus";

interface Props {
  // Rendered inside the wrapper. Receives the height the grid's scroll container
  // should use: undefined in normal flow (the table keeps its own maxHeight), or a
  // viewport-derived value in fullscreen so the grid fills the screen. With inlineToggle
  // it also receives the (icon-only) toggle to place in its own toolbar, null while full.
  children: (scrollMaxHeight: number | undefined, toggle: ReactNode) => ReactNode;
  // Shown in the fullscreen header bar so finance knows which table they expanded.
  label?: string;
  // The grid places the toggle itself, instead of on a row of its own above the grid.
  inlineToggle?: boolean;
}

// Chrome around the fullscreen panel: header bar + the wrapper's own padding. The
// grid's scroll box gets the rest of the viewport, so pinned columns and headers
// stay visible for a full-screen-height scroll (the point of the feature).
const CHROME_PX = 132;

// Wraps any grid with a "full screen" toggle. Fullscreen is rendered IN PLACE (a
// fixed-position overlay) rather than through a portal, so the grid keeps its React
// tree — every hook the table owns (column filters, pins, cell selection, frozen
// offsets, paging) survives the toggle with its state intact, and the sticky/pinned
// CSS keeps working because the scroll container is still the table's own ancestor.
export default function FullscreenTable({ children, label, inlineToggle = false }: Props) {
  const [full, setFull] = useState(false);
  const titleId = useId();
  const hostRef = useRef<HTMLDivElement | null>(null);
  useDialogFocus(full, hostRef);

  // Esc leaves fullscreen — the expected way out of any full-screen surface, and the
  // only one available when the toggle scrolls out of reach.
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  // The page behind the overlay must not scroll; restore whatever the page had
  // rather than hard-coding "" so a nested//pre-existing lock is not clobbered.
  useEffect(() => {
    if (!full) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [full]);

  // Track the viewport so the grid re-fits on resize, zoom, and mobile URL-bar show/
  // hide. Only while fullscreen, so a normal page pays no listener cost.
  const [vh, setVh] = useState(() => (typeof window === "undefined" ? 800 : window.innerHeight));
  useEffect(() => {
    if (!full) return;
    const measure = () => setVh(window.innerHeight);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [full]);

  const toggle = useCallback(() => setFull((f) => !f), []);

  // Floor the computed height so a very short viewport still leaves a usable grid
  // rather than collapsing it to nothing.
  const scrollMax = full ? Math.max(220, vh - CHROME_PX) : undefined;

  const button = (
    <button
      type="button"
      className="btn fullscreen-toggle"
      onClick={toggle}
      aria-pressed={full}
      title={full ? "Exit full screen (Esc)" : "Expand this table to full screen"}
    >
      {full ? <ExitIcon /> : <EnterIcon />}
      <span>{full ? "Exit full screen" : "Full screen"}</span>
    </button>
  );
  const inline = inlineToggle && !full;
  const inlineButton = inline ? (
    <button type="button" className="btn fullscreen-toggle fullscreen-toggle--icon"
      onClick={toggle} aria-pressed={false} aria-label="Expand this table to full screen"
      title="Expand this table to full screen">
      <EnterIcon />
    </button>
  ) : null;

  // ONE tree for both states — only classNames change. Returning two different
  // structures would give React a different element shape per toggle, remounting the
  // grid and wiping exactly the state this feature exists to preserve (filters, pins,
  // selection, page). Keeping the shape identical makes the toggle a pure re-style.
  return (
    <div ref={hostRef} className={full ? "fullscreen-overlay" : "fullscreen-host"}
      tabIndex={full ? -1 : undefined}
      role={full ? "dialog" : undefined}
      aria-modal={full ? true : undefined}
      aria-labelledby={full ? titleId : undefined}>
      <div className={full ? "fullscreen-head" : "fullscreen-bar" + (inline ? " fullscreen-bar--inline" : "")}>
        {full && <span className="fullscreen-title" id={titleId}>{label || "Table"}</span>}
        {!inline && button}
      </div>
      <div className={full ? "fullscreen-body" : undefined}>
        {children(scrollMax, inlineButton)}
      </div>
    </div>
  );
}

function EnterIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 1H1v5M10 1h5v5M10 15h5v-5M6 15H1v-5" />
    </svg>
  );
}

function ExitIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 6h5V1M15 6h-5V1M15 10h-5v5M1 10h5v5" />
    </svg>
  );
}
